import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ENV from "@config/env";
import Layout from "@components/layout/Layout";
import WorkflowPanels from "@components/form/WorkflowPanels";
import InfoPanel from "@components/form/InfoPanel";
import StepButton from "@components/shared/StepButton";
import Loading from "@components/shared/Loading";
import SelectLevel1WRS from "@components/form/subscriber/subscriber_sync_upload/SelectLevel1WRS";
import UploadFile from "@components/form/subscriber/subscriber_sync_upload/UploadFile";
import Review from "@components/form/subscriber/subscriber_sync_upload/Review";
import { useGetLevel1Resource } from "@hooks/query/useGetLevel1Resource";
import { StepProvider, useStepContext } from "@context/shared/StepContext";
import {
  SubscriberSyncUploadProvider,
  useSubscriberSyncUpload,
} from "@context/subscriberSyncUpload/Provider";
import { WizardStep } from "@context/subscriberSyncUpload/reducer";
import { getDraft } from "@services/subscriberSyncUpload";
import { toastError } from "@utils/toast";

const STEP_INDEX: Record<WizardStep, number> = {
  "select-scheme": 0,
  "upload-file": 1,
  review: 2,
};

const SubscriberSyncUpload = () => (
  <StepProvider maxStep={3}>
    <SubscriberSyncUploadProvider>
      <Consumer />
    </SubscriberSyncUploadProvider>
  </StepProvider>
);

const Consumer = () => {
  const { t } = useTranslation();
  const { id: routeLevel1ResourceId = "" } = useParams();
  const { currentStep, stepHelpers } = useStepContext();
  const { state, dispatch } = useSubscriberSyncUpload();
  // The route-bound entry pins the scheme via URL; the parent-level entry leaves the URL bare and
  // takes the scheme from wizard state once the operator picks one in step 0.
  const level1ResourceId = routeLevel1ResourceId || state.level1ResourceId || "";
  const { data: level1Resource, isLoading } = useGetLevel1Resource(level1ResourceId, {
    enabled: Boolean(level1ResourceId),
  });
  const isParentLevelEntry = !routeLevel1ResourceId;

  // Resume / View from the upload list: when a ?draftId is present, hydrate the saved record and jump
  // straight to the review step (mirrors the bulk-meter upload's draft resume). The draft is created
  // once the operator reaches the upload→review step and has an id, so this re-opens that same record
  // rather than starting a fresh wizard.
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draftId");
  const hydratedRef = React.useRef(false);
  const [hydrating, setHydrating] = React.useState(Boolean(draftId));

  React.useEffect(() => {
    if (!draftId || hydratedRef.current) return;
    hydratedRef.current = true;
    setHydrating(true);
    getDraft(draftId)
      .then(draft => {
        if (level1ResourceId) dispatch({ type: "select-scheme", level1ResourceId });
        dispatch({ type: "trial-run-loaded", uploadId: draft.uploadId, rows: draft.rows });
      })
      .catch(err =>
        toastError(
          t("subscriber_sync_upload.message.error", {
            error: err?.response?.data?.message ?? err?.message ?? "unknown",
          }),
        ),
      )
      .finally(() => setHydrating(false));
  }, [draftId, level1ResourceId, dispatch, t]);

  // Keep StepProvider in sync with the wizard reducer
  React.useEffect(() => {
    stepHelpers.setStep(STEP_INDEX[state.step]);
  }, [state.step, stepHelpers]);

  if ((Boolean(level1ResourceId) && isLoading) || hydrating) {
    return (
      <div className="pt-80">
        <Loading />
      </div>
    );
  }

  const templateDownloadUrl = `${process.env.PUBLIC_URL}${
    ENV.CLIENT_ID === "seqwater" ? "/seqwater" : ""
  }/bulk_customer_template.xlsx`;

  const steps = [
    {
      label: (
        <StepButton
          index={0}
          currentStep={currentStep}
          info={
            <p>
              <strong>{t("common.level1wrs")}: </strong>
              {level1Resource?.name}
            </p>
          }
        >
          {t("subscriber_sync_upload.step.select_scheme")}
        </StepButton>
      ),
      panel: <SelectLevel1WRS />,
    },
    {
      label: (
        <StepButton index={1} currentStep={currentStep}>
          {t("subscriber_sync_upload.step.upload_file")}
        </StepButton>
      ),
      panel: <UploadFile />,
      infoPanel: (
        <InfoPanel
          actions={[
            {
              label: t("subscriber_sync_upload.download_template"),
              action: () => {
                const link = document.createElement("a");
                link.href = templateDownloadUrl;
                link.download = "bulk_customer_template.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              },
            },
          ]}
        >
          <div className="space-y-4 leading-relaxed">
            <p>
              {t("subscriber_sync_upload.info.select_file", {
                level1WRSName: level1Resource?.name,
              })}
            </p>
            <p>{t("subscriber_sync_upload.info.template_description")}</p>
          </div>
        </InfoPanel>
      ),
    },
    {
      label: (
        <StepButton
          index={2}
          currentStep={currentStep}
          info={
            <p>
              <strong>{t("subscriber_sync_upload.rows_label")}: </strong>
              {state.rows.length}
            </p>
          }
        >
          {t("subscriber_sync_upload.step.review")}
        </StepButton>
      ),
      panel: <Review />,
      infoPanel: (
        <InfoPanel>
          <div className="space-y-4 leading-relaxed">
            <p>{t("subscriber_sync_upload.info.review")}</p>
            <ul className="pl-2 list-inside list-disc space-y-2">
              <li>{t("subscriber_sync_upload.info.review_has_differences")}</li>
              <li>{t("subscriber_sync_upload.info.review_in_sync")}</li>
              <li>{t("subscriber_sync_upload.info.review_invalid")}</li>
              <li>{t("subscriber_sync_upload.info.review_not_found")}</li>
            </ul>
          </div>
        </InfoPanel>
      ),
    },
  ];

  return (
    <Layout
      permissions={["BulkUploadSubscriberSync"]}
      breadcrumb={
        isParentLevelEntry
          ? [
              { label: t("common.home"), href: "/polestar" },
              {
                label: t("topnav.subscribers"),
                href: "/polestar/subscribers",
              },
              {
                label: t("topnav.customer_data_synchronisation"),
                href: "/polestar/subscribers/sync_uploads",
              },
              { label: t("subscriber_sync_upload.title.upload") },
            ]
          : [
              { label: t("common.home"), href: "/polestar" },
              {
                label: level1Resource?.name,
                href: `/polestar/level1wrs/${level1ResourceId}`,
              },
              {
                label: t("level1wrs.subscribers_and_users"),
                href: `/polestar/level1wrs/${level1ResourceId}#2`,
              },
              { label: t("subscriber_sync_upload.title.upload") },
            ]
      }
      title={t("subscriber_sync_upload.title.upload")}
    >
      {/* min-w-0 lets the content column honour its width allocation instead of growing to the
          Review table's intrinsic width; the table's own overflow-auto then scrolls horizontally,
          so the page (and the info panel) never overflow the viewport. */}
      <WorkflowPanels
        selectedStep={currentStep}
        steps={steps}
        contentClassName="min-w-0"
      />
    </Layout>
  );
};

export default SubscriberSyncUpload;
